import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebouncedCallback  } from 'use-debounce';
import useAuth from '../../../hooks/useAuth';
import api from '../../../api/axios';
import { getCountryCodes, getIndStates } from '../../../utils/common';
import { useLoading } from '../../../components/LoadingProvider';
import { showSuccess, showError } from '../../../utils/toast';
import { getPublicImgUrl } from '../../../utils/common';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

function ClinicPatients() {
    const navigate = useNavigate();
    const errorDivRef = useRef(null);
    const reportFileInputRef = useRef(null);
    const { user, isAuthenticated, userRole } = useAuth({ redirectPath: '/login', allowedRoles: ['clinic'] });
    const [patientsList, setPatientsList] = useState([]);
    const [firstPatientIndex, setFirstPatientIndex] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [paginationPages, setPaginationPages] = useState([]);
    const [totalPatients, setTotalPatients] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [patientsPerPage, setPatientsPerPage] = useState(12);
    const { setIsLoading } = useLoading();
    const MySwal = withReactContent(Swal);
    const countryCodes = getCountryCodes();
    const indStates = getIndStates();
    const [btnDisabled, setBtnDisabled] = useState(false);
    const [isPatientFormOpen, setIsPatientFormOpen] = useState(false);
    const [prefixOfName, setPrefixOfName] = useState([ 'Dr.', 'Prof.', 'Mr.', 'Mrs.', 'Ms.' ]);
    const initialPatientErrors = {
        name_prefix: '',
        name: '',
        dob: '',
        gender: '',
        phone_code: '',
        phone_number: '',
        email: '',
        address_line1: '',
        district: '',
        state: '',
        pincode: '',
        weight: '',
        height: '',
        blood_pressure: '',
        pulse_rate: '',
        oxygen_saturation: '',
        blood_sugar: ''
    };
    const debounced = useDebouncedCallback((value) => {
        setCurrentPage(1);
        setSearchText(value);
        fetchPatientsData(value);
    }, 1000);
    const [errors, setErrors] = useState(initialPatientErrors);

    const initialPatientFormData = {
        name_prefix: '',
        name: '',
        dob: '',
        age: '',
        gender: '',
        phone_code: '',
        phone_number: '',
        email: '',
        address_line1: '',
        address_line2: '',
        district: '',
        state: '',
        pincode: '',
        country: 'India',
        allergies: '',
        regular_medicines: '',
        weight: '',
        height: '',
        blood_pressure: '',
        pulse_rate: '',
        oxygen_saturation: '',
        blood_sugar: '',
        previous_reports_uploaded: []
    };

    const [patientFormData, setPatientFormData] = useState(initialPatientFormData);

    const fetchPatientsData = async (search = '') => {
        try {
            setIsLoading(true);
            const response = await api.get(`/${userRole}/patients?search=${search}&limit=${patientsPerPage}&currentpage=${currentPage}`);
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                setPatientsList(response?.data?.data?.patients || []);
                setTotalPatients(response?.data?.data?.total_row_count || 0);
                const tp = Math.ceil(response?.data?.data?.total_row_count / patientsPerPage)
                setTotalPages(tp);
                setFirstPatientIndex((currentPage - 1) * patientsPerPage);
                setPaginationPages(Array.from({ length: tp }, (_, index) => index + 1));
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Something went wrong! Please try again later.');
            if (error.response?.status === 401) {
                navigate('/login');
            }
        } finally {
            setIsLoading(false);
        }
    };
    const handlePatientEdit = async (id) => {
        try {
            setIsLoading(true);
            const response = await api.get(`/${userRole}/patient/${id}`);
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                setPatientFormData(response?.data?.data?.patient);
                setIsPatientFormOpen(true);
                if (typeof window !== 'undefined') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Something went wrong! Please try again later.');
            if (error.response?.status === 401) {
                navigate('/login');
            }
        } finally {
            setIsLoading(false);
        }
    }
    const handleResetPatientForm = () => {
        setPatientFormData(initialPatientFormData);
    }
    const deletePatientPopop = (id) => {
        MySwal.fire({
            title: 'Are you sure?',
            text: 'You want to delete this patient?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, keep it',
        }).then((result) => {
            if (result.isConfirmed) {
                handleDeletePatient(id);
            }
        });
    }
    const handleDeletePatient = async (id) => {
        try {
            setIsLoading(true);
            const response = await api.delete(`/${userRole}/patient/delete/${id}`);
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                fetchPatientsData(searchText);
                setPatientFormData(initialPatientFormData);
                setErrors(initialPatientErrors);
                if (typeof window !== 'undefined') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Something went wrong! Please try again later.');
            if (error.response?.status === 401) {
                navigate('/login');
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        document.title = 'Varosa - Clinic Patients';
        if (!isAuthenticated || !user) {
            navigate('/login');
            return;
        }
    }, [isAuthenticated, user, navigate]);

    useEffect(() => {
        if(!isPatientFormOpen){
            setPatientFormData(initialPatientFormData);
        }
    }, [isPatientFormOpen]);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages || page === currentPage) {
            return;
        }
        setCurrentPage(page);
    };
    useEffect(() => {
        fetchPatientsData(searchText);
    }, [currentPage]);

    const validateForm = () => {
        let all_good = true;
        let error = {
            name_prefix: '',
            name: '',
            dob: '',
            gender: '',
            phone_code: '',
            phone_number: '',
            email: '',
            address_line1: '',
            district: '',
            state: '',
            pincode: '',
            weight: '',
            height: '',
            blood_pressure: '',
            pulse_rate: '',
            oxygen_saturation: '',
            blood_sugar: ''
        };

        const getValue = (key) => String(patientFormData[key] || '').trim();
        const isFilledNumber = (key) => getValue(key) !== '' && !Number.isNaN(Number(getValue(key)));

        if (getValue('name_prefix') === '' || getValue('name_prefix') === 'Choose...') {
            error.name_prefix = 'Please select a valid prefix.';
            all_good = false;
        }
        if (getValue('name') === '' || getValue('name').length < 3) {
            error.name = 'Please enter a valid patient name.';
            all_good = false;
        }
        if (getValue('dob') === '') {
            error.dob = 'Please enter a valid DOB.';
            all_good = false;
        } else {
            const dob = new Date(getValue('dob'));
            const today = new Date();
            const minDob = new Date();
            minDob.setFullYear(today.getFullYear() - 120);

            if (Number.isNaN(dob.getTime())) {
                error.dob = 'Please enter a valid DOB.';
                all_good = false;
            } else if (dob > today) {
                error.dob = 'DOB cannot be in the future.';
                all_good = false;
            } else if (dob < minDob) {
                error.dob = 'The maximum allowed age is 120 years.';
                all_good = false;
            }
        }
        if (!['male', 'female', 'other'].includes(getValue('gender'))) {
            error.gender = 'Please select a valid gender.';
            all_good = false;
        }
        if (getValue('phone_code') === '' || getValue('phone_code') === 'Choose...') {
            error.phone_code = 'Please select a phone code.';
            all_good = false;
        }
        if (getValue('phone_number') === '' || !/^\d{7,15}$/.test(getValue('phone_number'))) {
            error.phone_number = 'Please enter a valid contact number.';
            all_good = false;
        }
        if (getValue('email') !== '' && !/^\S+@\S+\.\S+$/.test(getValue('email'))) {
            error.email = 'Please enter a valid email.';
            all_good = false;
        }
        if (getValue('address_line1') === '') {
            error.address_line1 = 'Please enter a valid address.';
            all_good = false;
        }
        if (getValue('district') === '') {
            error.district = 'Please enter a valid district.';
            all_good = false;
        }
        if (getValue('state') === '') {
            error.state = 'Please select a valid state.';
            all_good = false;
        }
        if (getValue('pincode') === '' || !/^\d{6}$/.test(getValue('pincode'))) {
            error.pincode = 'Please enter a valid pincode (6-digits).';
            all_good = false;
        }
        // if (getValue('weight') !== '' && (!isFilledNumber('weight') || Number(getValue('weight')) <= 0)) {
        //     error.weight = 'Please enter a valid weight.';
        //     all_good = false;
        // }
        // if (getValue('height') !== '' && (!isFilledNumber('height') || Number(getValue('height')) <= 0)) {
        //     error.height = 'Please enter a valid height.';
        //     all_good = false;
        // }
        // if (getValue('blood_pressure') !== '' && !/^\d{2,3}\s*\/\s*\d{2,3}$/.test(getValue('blood_pressure'))) {
        //     error.blood_pressure = 'Please enter blood pressure like 120/80.';
        //     all_good = false;
        // }
        // if (getValue('pulse_rate') !== '' && (!isFilledNumber('pulse_rate') || Number(getValue('pulse_rate')) <= 0)) {
        //     error.pulse_rate = 'Please enter a valid pulse rate.';
        //     all_good = false;
        // }
        // if (getValue('oxygen_saturation') !== '' && (!isFilledNumber('oxygen_saturation') || Number(getValue('oxygen_saturation')) < 0 || Number(getValue('oxygen_saturation')) > 100)) {
        //     error.oxygen_saturation = 'Please enter oxygen saturation between 0 and 100.';
        //     all_good = false;
        // }
        // if (getValue('blood_sugar') !== '' && (!isFilledNumber('blood_sugar') || Number(getValue('blood_sugar')) <= 0)) {
        //     error.blood_sugar = 'Please enter a valid blood sugar value.';
        //     all_good = false;
        // }
        
        setErrors(error);
        return all_good;
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        for(let file of files){
            const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
            if (!validTypes.includes(file.type)) {
                showError('Please upload a PDF, PNG, JPEG file.');
                reportFileInputRef.current.value = '';
                return;
            }
            if(file.size > 2 * 1024 * 1024){
                showError('File size exceeds 2MB. Please choose a smaller file.');
                reportFileInputRef.current.value = '';
                return;
            }
        }
        setPatientFormData(prev => ({
            ...prev,
            previous_reports_uploaded: ((Array.isArray(prev.previous_reports_uploaded) ? [...prev.previous_reports_uploaded, ...files] : [...files]) || []),
        }));
        reportFileInputRef.current.value = '';
    };

    const handleRemoveFile = (index) => {
        const updatedReports = patientFormData.previous_reports_uploaded.filter((_, i) => i !== index);
        setPatientFormData(prev => ({
            ...prev,
            previous_reports_uploaded: updatedReports,
        }));
    };

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const viewReport = async (report, isItUrl = false) => {
        try{
            if(isItUrl){
                MySwal.fire({
                    title: 'Report',
                    html: `<iframe src="${report}" alt="Report" style="width: 100%;height: 600px;"></iframe>`,
                    width: '90%',
                    showConfirmButton: true,
                    confirmButtonText: 'Close',
                    customClass: {
                        container: '',
                    },
                });
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                const base64String = reader.result;
                if(base64String){
                    MySwal.fire({
                        title: 'Report',
                        html: `<iframe src="${base64String}" alt="Report" style="width: 100%;height: 600px;"></iframe>`,
                        width: '90%',
                        showConfirmButton: true,
                        confirmButtonText: 'Close',
                        customClass: {
                            container: '',
                        },
                    });
                }
            };
            reader.readAsDataURL(report);
        }catch(error){
            console.log(error);
            showError('Something went wrong! Please try again later.');
            return;
        }
    };
    const handlePatientInputChange = (e) => {
        const { name, value } = e.target;
        setPatientFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if(!validateForm()){
                errorDivRef.current?.scrollIntoView({ behavior: 'smooth' });
                return; 
            }
            const postData = new FormData();
            const pid = patientFormData.id || '';
            postData.append('id', pid || '');
            postData.append('name_prefix', patientFormData.name_prefix || '');
            postData.append('name', patientFormData.name || '');
            postData.append('dob', patientFormData.dob || '');
            postData.append('age', patientFormData.age || '');
            postData.append('gender', patientFormData.gender || '');
            postData.append('phone_code', patientFormData.phone_code || '');
            postData.append('phone_number', patientFormData.phone_number || '');
            postData.append('email', patientFormData.email || '');
            postData.append('address_line1', patientFormData.address_line1 || '');
            postData.append('address_line2', patientFormData.address_line2 || '');
            postData.append('district', patientFormData.district || '');
            postData.append('state', patientFormData.state || '');
            postData.append('pincode', patientFormData.pincode || '');
            postData.append('country', patientFormData.country || '');
            postData.append('allergies', patientFormData.allergies || '');
            postData.append('regular_medicines', patientFormData.regular_medicines || '');
            postData.append('weight', patientFormData.weight || '');
            postData.append('height', patientFormData.height || '');
            postData.append('blood_pressure', patientFormData.blood_pressure || '');
            postData.append('pulse_rate', patientFormData.pulse_rate || '');
            postData.append('oxygen_saturation', patientFormData.oxygen_saturation || '');
            postData.append('blood_sugar', patientFormData.blood_sugar || '');

            if (Array.isArray(patientFormData.previous_reports_uploaded)) {
                for (let report of patientFormData.previous_reports_uploaded) {
                    if (report.id) {
                        postData.append('existing_previous_reports_ids[]', report.id);
                    } else {
                        postData.append('previous_reports[]', report);
                    }
                }
            }

            setBtnDisabled(true);
            setIsLoading(true);
            const response = await api.post('/'+userRole+'/patient/store'+(pid ? '/'+pid : ''), postData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response?.data?.result === 'success') {
                showSuccess(response.data.message);
                setPatientFormData(initialPatientFormData);
                setErrors(initialPatientErrors);
                setIsPatientFormOpen(false);
                fetchPatientsData(searchText);
                if (typeof window !== 'undefined') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            console.log(error)
            showError(error.response?.data?.message || 'Error updating profile');
        } finally {
            setBtnDisabled(false);
            setIsLoading(false);
        }
    };

    return (
        <>
        <div className="container py-5 login-container">
            <div className="row g-3">
                <div className="col-9">
                    <h2 className="mb-4">Clinic's Patients</h2>
                </div>
                <div className="col-3 text-end">
                    <button onClick={() => window.history.go(-1)} className="btn btn-primary">Back</button>
                </div>
            </div>
            <div className="row">
                <div className="col-12">
                    <button
                        className="btn btn-primary"
                        type="button"
                        aria-expanded={isPatientFormOpen}
                        aria-controls="addPatientForm"
                        onClick={() => setIsPatientFormOpen(prev => !prev)}
                    >
                        {isPatientFormOpen ? 'Hide Add Patient Form' : 'Add Patient'}
                    </button>
                    <div className={`collapse mt-4 ${isPatientFormOpen ? 'show' : ''}`} id="addPatientForm">
                        <div className="card border-0 shadow-sm">
                            <div className="card-body">
                                <form onSubmit={handleSubmit} ref={errorDivRef}>
                                    <div className="border-start border-5 border-primary bg-light px-3 py-2 mb-3">
                                        <h4 className="text-primary mb-0">Patient Registration Form {patientFormData.id ? 'ID: '+patientFormData.id : ''}</h4>
                                    </div>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label htmlFor="patient_name" className="form-label fw-semibold">Patient Name</label>
                                            <div className="input-group mb-3">
                                                <select 
                                                    className="form-select py-3 border-primary"
                                                    name="name_prefix"
                                                    value={patientFormData.name_prefix}
                                                    onChange={handlePatientInputChange}
                                                    style={{ maxWidth: '120px' }}
                                                >
                                                    <option>Choose...</option>
                                                    {prefixOfName.map((prefix) => (
                                                        <option key={prefix} value={prefix}>{prefix}</option>
                                                    ))}
                                                </select>
                                                <input type="text" className="form-control py-3 border-primary" id="name" name="name" value={patientFormData.name} onChange={handlePatientInputChange} />
                                            </div>
                                            {errors.name_prefix && <div className="invalid-feedback d-block">{errors.name_prefix}</div>}
                                            {errors.name && <div className="invalid-feedback d-block">{errors.name}</div>}
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label">DOB</label>
                                            <input
                                                type="date"
                                                className="form-control py-3 border-primary"
                                                name="dob"
                                                value={patientFormData.dob}
                                                onChange={handlePatientInputChange}
                                                max={new Date().toISOString().split('T')[0]}
                                            />
                                            {errors.dob && <div className="invalid-feedback d-block">{errors.dob}</div>}
                                        </div>
                                        <div className="col-md-3 d-none">
                                            <label htmlFor="age" className="form-label fw-semibold">Age</label>
                                            <input type="number" min="0" className="form-control py-3 border-primary" id="age" name="age" value={patientFormData.age} onChange={handlePatientInputChange} />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label fw-semibold d-block">Gender</label>
                                            <div className="d-flex flex-wrap gap-3">
                                                <select className="form-select py-3 border-primary" name="gender" value={patientFormData.gender} onChange={handlePatientInputChange}>
                                                    <option value="">Choose...</option>
                                                    <option value="male">Male</option>
                                                    <option value="female">Female</option>
                                                    <option value="other">Other</option>
                                                </select>
                                            </div>
                                            {errors.gender && <div className="invalid-feedback d-block">{errors.gender}</div>}
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Contact Number</label>
                                            <div className="input-group mb-3">
                                                <select 
                                                    className="form-select py-3 border-primary"
                                                    name="phone_code"
                                                    value={patientFormData.phone_code}
                                                    onChange={handlePatientInputChange}
                                                    style={{ maxWidth: '120px' }}
                                                >
                                                    <option>Choose...</option>
                                                    {countryCodes.map((country) => (
                                                        <option key={country.code} value={country.code}>
                                                            {country.code}
                                                        </option>
                                                    ))}
                                                </select>
                                                <input
                                                    type="tel"
                                                    className="form-control py-3 border-primary"
                                                    name="phone_number"
                                                    value={patientFormData.phone_number}
                                                    onChange={handlePatientInputChange}
                                                />
                                            </div>
                                            {errors.phone_code && <div className="invalid-feedback d-block">{errors.phone_code}</div>}
                                            {errors.phone_number && <div className="invalid-feedback d-block">{errors.phone_number}</div>}
                                        </div>
                                        <div className="col-md-6">
                                            <label htmlFor="email" className="form-label fw-semibold">Email (Optional)</label>
                                            <input type="email" className="form-control py-3 border-primary" id="email" name="email" value={patientFormData.email} onChange={handlePatientInputChange} />
                                            {errors.email && <div className="invalid-feedback d-block">{errors.email}</div>}
                                        </div>
                                        <div className="col-md-6">
                                            <label htmlFor="address_line1" className="form-label fw-semibold">Address Line 1</label>
                                            <input type="text" className="form-control py-3 border-primary" id="address_line1" name="address_line1" value={patientFormData.address_line1} onChange={handlePatientInputChange} />
                                            {errors.address_line1 && <div className="invalid-feedback d-block">{errors.address_line1}</div>}
                                        </div>
                                        <div className="col-md-6">
                                            <label htmlFor="address_line2" className="form-label fw-semibold">Address Line 2</label>
                                            <input type="text" className="form-control py-3 border-primary" id="address_line2" name="address_line2" value={patientFormData.address_line2} onChange={handlePatientInputChange} />
                                        </div>
                                        <div className="col-md-4">
                                            <label htmlFor="district" className="form-label fw-semibold">District</label>
                                            <input type="text" className="form-control py-3 border-primary" id="district" name="district" value={patientFormData.district} onChange={handlePatientInputChange} />
                                            {errors.district && <div className="invalid-feedback d-block">{errors.district}</div>}
                                        </div>
                                        <div className="col-md-4">
                                            <label htmlFor="state" className="form-label fw-semibold">State</label>
                                            <select
                                                className="form-select py-3 border-primary"
                                                name="state"
                                                value={patientFormData.state}
                                                onChange={handlePatientInputChange}
                                            >
                                                <option value="">Choose...</option>
                                                {indStates.map((val) => (
                                                    <option key={val.key} value={val.key}>
                                                        {val.name}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.state && <div className="invalid-feedback d-block">{errors.state}</div>}
                                        </div>
                                        <div className="col-md-4">
                                            <label htmlFor="pincode" className="form-label fw-semibold">Pincode</label>
                                            <input type="text" className="form-control py-3 border-primary" id="pincode" name="pincode" value={patientFormData.pincode} onChange={handlePatientInputChange} />
                                            {errors.pincode && <div className="invalid-feedback d-block">{errors.pincode}</div>}
                                        </div>
                                        <div className="col-md-6">
                                            <label htmlFor="allergies" className="form-label fw-semibold">Known Allergies (Optional)</label>
                                            <textarea className="form-control py-3 border-primary" id="allergies" name="allergies" rows="2" value={patientFormData.allergies} onChange={handlePatientInputChange}></textarea>
                                            {errors.allergies && <div className="invalid-feedback d-block">{errors.allergies}</div>}
                                        </div>
                                        <div className="col-md-6">
                                            <label htmlFor="regular_medicines" className="form-label fw-semibold">Regular Medicines (Optional)</label>
                                            <textarea className="form-control py-3 border-primary" id="regular_medicines" name="regular_medicines" rows="2" value={patientFormData.regular_medicines} onChange={handlePatientInputChange}></textarea>
                                            {errors.regular_medicines && <div className="invalid-feedback d-block">{errors.regular_medicines}</div>}
                                        </div>
                                    </div>

                                    <div className="border-start border-5 border-primary bg-light px-3 py-2 my-4">
                                        <h4 className="text-primary mb-0">Clinical Vitals & Triage</h4>
                                    </div>
                                    <div className="row g-3">
                                        <div className="col-md-4 col-lg-2">
                                            <label htmlFor="weight" className="form-label fw-semibold">Weight (kg) (Optional)</label>
                                            <input type="text" className="form-control py-3 border-primary" id="weight" name="weight" value={patientFormData.weight} onChange={handlePatientInputChange} />
                                            {errors.weight && <div className="invalid-feedback d-block">{errors.weight}</div>}
                                        </div>
                                        <div className="col-md-4 col-lg-2">
                                            <label htmlFor="height" className="form-label fw-semibold">Height (Optional)</label>
                                            <input type="text" className="form-control py-3 border-primary" id="height" name="height" value={patientFormData.height} onChange={handlePatientInputChange} />
                                            {errors.height && <div className="invalid-feedback d-block">{errors.height}</div>}
                                        </div>
                                        <div className="col-md-4 col-lg-2">
                                            <label htmlFor="blood_pressure" className="form-label fw-semibold">Blood Pressure (Optional)</label>
                                            <input type="text" className="form-control py-3 border-primary" id="blood_pressure" name="blood_pressure" value={patientFormData.blood_pressure} onChange={handlePatientInputChange} />
                                            {errors.blood_pressure && <div className="invalid-feedback d-block">{errors.blood_pressure}</div>}
                                        </div>
                                        <div className="col-md-4 col-lg-2">
                                            <label htmlFor="pulse_rate" className="form-label fw-semibold">Pulse Rate (bpm) (Optional)</label>
                                            <input type="text" className="form-control py-3 border-primary" id="pulse_rate" name="pulse_rate" value={patientFormData.pulse_rate} onChange={handlePatientInputChange} />
                                            {errors.pulse_rate && <div className="invalid-feedback d-block">{errors.pulse_rate}</div>}
                                        </div>
                                        <div className="col-md-4 col-lg-2">
                                            <label htmlFor="oxygen_saturation" className="form-label fw-semibold">Oxygen Sat. (SpO2) (Optional)</label>
                                            <input type="text" min="0" max="100" className="form-control py-3 border-primary" id="oxygen_saturation" name="oxygen_saturation" value={patientFormData.oxygen_saturation} onChange={handlePatientInputChange} />
                                            {errors.oxygen_saturation && <div className="invalid-feedback d-block">{errors.oxygen_saturation}</div>}
                                        </div>
                                        <div className="col-md-4 col-lg-2">
                                            <label htmlFor="blood_sugar" className="form-label fw-semibold">Blood Sugar (Opt) (Optional)</label>
                                            <input type="text" min="0" step="0.1" className="form-control py-3 border-primary" id="blood_sugar" name="blood_sugar" value={patientFormData.blood_sugar} onChange={handlePatientInputChange} />
                                            {errors.blood_sugar && <div className="invalid-feedback d-block">{errors.blood_sugar}</div>}
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label">Previous Reports and Prescriptions (optional)</label>
                                            <input
                                                type="file"
                                                className="form-control py-3 border-primary"
                                                onChange={handleFileUpload}
                                                multiple
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                ref={reportFileInputRef}
                                            />
                                            {errors.previous_reports_uploaded && <div className="invalid-feedback d-block">{errors.previous_reports_uploaded}</div>}
                                            <div className="text-muted mt-2 mb-2">
                                                <small>PDF, JPEG, JPG, or PNG. Max size 2MB per file.</small>
                                            </div>
                                            {patientFormData.previous_reports_uploaded.length > 0 && (
                                                <div className="mt-2">
                                                    <p>Selected files:</p>
                                                    <ul className="list-group">
                                                        {patientFormData.previous_reports_uploaded.map((file, index) => (
                                                            file.id ? 
                                                            (<li key={index} className="list-group-item">
                                                                {file.original_name} ({formatBytes(file.file_size)})
                                                                <span className="float-end">
                                                                    <i 
                                                                        className="fas fa-eye text-primary me-3" 
                                                                        onClick={() => viewReport(file.file_path, true)}
                                                                        style={{cursor: 'pointer'}}></i>
                                                                    <i 
                                                                        className="fas fa-trash-alt text-danger" 
                                                                        onClick={() => handleRemoveFile(index)} 
                                                                        style={{cursor: 'pointer'}}></i>
                                                                </span>
                                                            </li>)
                                                            : (<li key={index} className="list-group-item">
                                                                {file.name} ({formatBytes(file.size)})
                                                                <span className="float-end">
                                                                    <i 
                                                                        className="fas fa-eye text-primary me-3" 
                                                                        onClick={() => viewReport(file, false)}
                                                                        style={{cursor: 'pointer'}}></i>
                                                                    <i 
                                                                        className="fas fa-trash-alt text-danger" 
                                                                        onClick={() => handleRemoveFile(index)} 
                                                                        style={{cursor: 'pointer'}}></i>
                                                                </span>
                                                            </li>)
                                                        ))}
                                                    </ul> 
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-12 text-end">
                                            <button type="reset" className="btn btn-danger me-3" disabled={btnDisabled} onClick={handleResetPatientForm}>Reset</button>
                                            <button type="submit" className="btn btn-primary" disabled={btnDisabled}>Save Patient</button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div className="container-fluid service bg-light py-5">
            <div className="container">
                <div className="row g-4 justify-content-center">
                    <div className="col-md-12 justify-content-center align-items-center text-center col-sm-12">
                        <div className="offset-md-3 col-md-6">
                            <form>
                                <div className="input-group mb-3" title="Search by Patient Name / Number">
                                    <input type="text" className="form-control py-3 border-primary bg-transparent" placeholder="Search by Patient Name / Number" onChange={(e) => {
                                        debounced(e.target.value);
                                    }} />
                                    <button type="button" className="btn btn-primary text-white py-1 px-3">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-search" viewBox="0 0 16 16">
                                            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
                                        </svg>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            <div className="container py-2">
                <div className="row g-4 mt-2 justify-content-center">
                    {patientsList.map(patient => 
                    (<div key={patient.id} className="col-md-4 col-sm-6 col-xs-12 wow fadeInUp" data-wow-delay="0.1s">
                        <div className="team-item rounded">
                            <div className="team-img rounded-top h-100 border border-primary border-bottom-0 p-2">
                                <img src={patient.profile_picture ? patient.profile_picture : getPublicImgUrl()+'/profile_8747415.png'} className="img-fluid rounded-top w-100" style={{ maxHeight: '250px', objectFit: 'contain' }} alt="" />
                            </div>
                            <div className="team-content text-center border border-primary border-top-0 rounded-bottom p-4">
                                <h5 className="mb-4">{patient.formated_name}</h5>
                                <p className="mb-0">
                                    <b>Ph:</b> {patient.phone_code+' '+patient.phone_number}<br />
                                    <b>DOB:</b> {patient.formated_dob}<br />
                                    <b>Gender:</b> {patient.formated_gender}
                                </p>
                                <hr />
                                <button onClick={(e) => { 
                                    e.preventDefault();
                                    navigate(`/dashboard/clinic/appointments/?pid=${patient.id}`)
                                }} className="btn btn-success text-white me-3 mb-3">Appointments</button>
                                <button onClick={(e) => { 
                                    e.preventDefault();
                                    handlePatientEdit(patient.id);
                                }} className="btn btn-primary text-white me-3 mb-3">Edit</button>
                                <button onClick={(e) => { 
                                    e.preventDefault();
                                    deletePatientPopop(patient.id);
                                }} className="btn btn-danger text-white me-3 mb-3">Delete</button>
                            </div>
                        </div>
                    </div>))}
                    {patientsList.length === 0 && <div className="col-12 text-center">
                        <h5 className="m-4">No Patient Found</h5>
                    </div>}
                </div>
                {totalPages > 1 && (
                    <div className="row mt-4">
                        <div className="col-md-6 text-center text-md-start mb-3 mb-md-0">
                            <span className="text-muted">
                                Showing {firstPatientIndex + 1}-{Math.min(firstPatientIndex + patientsPerPage, totalPatients)} of {totalPatients} patients
                            </span>
                        </div>
                        <div className="col-md-6">
                            <nav aria-label="Patients pagination" className="shadow-none">
                                <ul className="pagination justify-content-center justify-content-md-end mb-0">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button
                                            type="button"
                                            className="page-link"
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        >
                                            Previous
                                        </button>
                                    </li>
                                    {paginationPages.map(page => (
                                        <li key={page} className={`page-item ${page === currentPage ? 'active' : ''}`}>
                                            <button
                                                type="button"
                                                className="page-link"
                                                onClick={() => handlePageChange(page)}
                                            >
                                                {page}
                                            </button>
                                        </li>
                                    ))}
                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                        <button
                                            type="button"
                                            className="page-link"
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        >
                                            Next
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                )}
            </div>
        </div>
        </>
    );
}

export default ClinicPatients;
